// Cache of friends' latest received profiles (ARCHITECTURE.md §8).
//
// When we open an envelope from a friend, we store the decrypted ProfileRecord
// here, keyed by the author's Circle ID. This is the "last known" copy shown
// offline and replaced only by a strictly newer version.

import { idbDelete, idbGet, idbPut, STORE_CACHE } from './db';
import { normalizeProfile, type ProfileRecord } from './profile';

export async function getCachedProfile(circleId: string): Promise<ProfileRecord | undefined> {
  const stored = await idbGet<ProfileRecord>(STORE_CACHE, circleId);
  // A friend may still be on an older client whose cached profile predates
  // `addresses` — normalize on read so it renders instead of crashing.
  return stored && { ...stored, profile: normalizeProfile(stored.profile) };
}

/**
 * Whether we hold a received profile from this Circle ID. Having their profile
 * means they added us and shared it — i.e. the connection is mutual.
 */
export async function hasReceivedFrom(circleId: string): Promise<boolean> {
  return (await getCachedProfile(circleId)) !== undefined;
}

export function removeCachedProfile(circleId: string): Promise<undefined> {
  return idbDelete(STORE_CACHE, circleId);
}

/**
 * Store a received profile if it is strictly newer than what we have.
 * Returns true if the cache was updated.
 */
export async function cacheProfileIfNewer(circleId: string, record: ProfileRecord): Promise<boolean> {
  const existing = await getCachedProfile(circleId);
  if (existing && existing.version >= record.version) return false;
  await idbPut(STORE_CACHE, circleId, record);
  return true;
}
